// Service data for dedicated SEO service pages
export const SERVICES_SEO = [
  {
    slug: "llc-formation", category: "Formation", name: "LLC Formation",
    h1: "LLC Formation Services",
    metaTitle: "LLC Formation Services | Start Your LLC Fast | Alexa Financial",
    metaDescription: "Form your LLC the right way with Alexa Financial Multiservices. State filing, registered agent, EIN and operating agreement included. Serving Maryland & nationwide.",
    keywords: "LLC formation, form an LLC, start an LLC, registered agent, operating agreement",
    intro: "Launching a business starts with the right legal foundation. We handle your entire LLC formation — from name availability and state filing to your registered agent, operating agreement and EIN — so you can open a bank account, build credit and operate with confidence from day one.",
    benefits: [
      "Fast, accurate state filing handled for you",
      "EIN (Federal Tax ID) secured for banking and hiring",
      "Custom operating agreement to protect your ownership",
      "Registered agent service to stay compliant",
      "Personalized guidance from a real advisor",
      "Foundation ready for business credit and funding",
    ],
    process: [
      { title: "Discovery & Name Check", desc: "We confirm your business name and map the right structure for your goals." },
      { title: "State Filing", desc: "We prepare and submit your Articles of Organization to the state." },
      { title: "EIN & Documents", desc: "We secure your EIN and deliver your operating agreement and compliance kit." },
      { title: "Growth Ready", desc: "We position your new entity to build business credit and access funding." },
    ],
    faqs: [
      { q: "How long does it take to form an LLC?", a: "Most filings are processed within 24–72 hours depending on the state, with expedited options available." },
      { q: "Do I need an EIN for my LLC?", a: "Yes. An EIN is required to open a business bank account, hire employees and file taxes — and it's included in our packages." },
      { q: "What is a registered agent?", a: "A registered agent receives legal and state documents on behalf of your LLC. We can serve as your agent to keep you compliant." },
      { q: "Can you form an LLC in any state?", a: "Yes. We form LLCs nationwide and tailor the filing to your specific state's requirements." },
    ],
    related: ["ein-registration", "business-credit", "business-funding"],
  },
  {
    slug: "ein-registration", category: "Formation", name: "EIN Registration",
    h1: "EIN Registration Services",
    metaTitle: "EIN Registration Service | Federal Tax ID | Alexa Financial",
    metaDescription: "Get your EIN (Federal Tax ID) quickly and correctly. Required for business banking, hiring and taxes. Alexa Financial Multiservices — Maryland & nationwide.",
    keywords: "EIN registration, federal tax ID, apply for EIN, business tax ID",
    intro: "Your EIN is the Social Security number of your business. We register your Federal Tax ID accurately so you can open a business bank account, hire employees, apply for funding and file taxes — without the IRS paperwork headaches.",
    benefits: [
      "EIN obtained directly from the IRS",
      "Required for business banking and payroll",
      "Foundation for business credit profiles",
      "Accurate filing to avoid IRS delays",
      "Guidance on responsible-party details",
      "Same-day handling in most cases",
    ],
    process: [
      { title: "Verify Entity", desc: "We confirm your LLC or entity details and responsible party." },
      { title: "Prepare Application", desc: "We complete the SS-4 accurately to prevent rejections." },
      { title: "Secure EIN", desc: "We obtain and deliver your EIN confirmation letter." },
      { title: "Activate", desc: "Use your EIN to bank, hire and build credit immediately." },
    ],
    faqs: [
      { q: "What is an EIN used for?", a: "An EIN is used to open bank accounts, hire staff, file taxes and establish business credit." },
      { q: "Do sole proprietors need an EIN?", a: "It's optional for sole proprietors but strongly recommended to separate personal and business finances." },
      { q: "How fast can I get my EIN?", a: "In most cases we can secure your EIN the same business day." },
      { q: "Is the EIN included with LLC formation?", a: "Yes — EIN registration is included in our LLC formation packages." },
    ],
    related: ["llc-formation", "business-credit", "business-funding"],
  },
  {
    slug: "business-funding", category: "Finance", name: "Business Funding",
    h1: "Business Funding & Capital Access",
    metaTitle: "Business Funding Services | Access Capital | Alexa Financial",
    metaDescription: "Access business funding with a lender-ready profile. Strategic capital for growth — no personal guarantee strategies available. Maryland & nationwide.",
    keywords: "business funding, small business loans, business capital, startup funding",
    intro: "Funding is a tool, not a trophy. We build a lender-ready profile and position your business for the right capital, at the right cost, at the right time — aligned to a growth plan that actually makes the money work for you.",
    benefits: [
      "Lender-ready business profile",
      "Strategic funding aligned to growth",
      "No-personal-guarantee strategies where possible",
      "Guidance on the right capital stack",
      "Support from application to approval",
      "Positioning that protects your personal credit",
    ],
    process: [
      { title: "Assessment", desc: "We review your goals, revenue and credit to map funding options." },
      { title: "Profile Build", desc: "We prepare a lender-ready business and credit profile." },
      { title: "Positioning", desc: "We match and submit to aligned lenders and programs." },
      { title: "Growth", desc: "We help you deploy capital strategically for ROI." },
    ],
    faqs: [
      { q: "Can I get funding without a personal guarantee?", a: "Once your business credit and profile are established, we position you for financing that relies on your business rather than your personal credit." },
      { q: "How much funding can my business access?", a: "It depends on your profile, credit and revenue. We build a strategy to maximize what you qualify for responsibly." },
      { q: "Do I need an LLC to get business funding?", a: "A properly structured entity and EIN dramatically improve your funding options — we can set this up first." },
      { q: "How long does funding take?", a: "Timelines vary by program, but a strong profile speeds approvals significantly." },
    ],
    related: ["business-credit", "llc-formation", "business-consulting"],
  },
  {
    slug: "business-credit", category: "Finance", name: "Business Credit",
    h1: "Business Credit Building",
    metaTitle: "Business Credit Building Services | Alexa Financial Multiservices",
    metaDescription: "Build business credit that separates you from your business. Establish tradelines and a strong profile to borrow without personal guarantees.",
    keywords: "business credit, build business credit, business tradelines, Paydex",
    intro: "Business credit separates you from your business, protects your personal assets and multiplies what you can build. We establish your profile, add the right tradelines and grow your business credit so lenders and vendors say yes.",
    benefits: [
      "Business credit profile setup",
      "Strategic tradelines that report",
      "Separation of personal and business credit",
      "Stronger lending and vendor terms",
      "Roadmap to higher limits over time",
      "Foundation for no-PG funding",
    ],
    process: [
      { title: "Profile Setup", desc: "We establish your business with the major bureaus and a D-U-N-S profile." },
      { title: "Tradelines", desc: "We add starter tradelines that report and build history." },
      { title: "Growth", desc: "We layer in higher-tier accounts and monitor progress." },
      { title: "Leverage", desc: "We position your profile for funding and better terms." },
    ],
    faqs: [
      { q: "What is business credit?", a: "Business credit is your company's ability to borrow in its own name, separate from your personal credit." },
      { q: "How long does it take to build business credit?", a: "Foundational profiles can form in weeks; strong, fundable profiles typically build over a few months." },
      { q: "Do I need a D-U-N-S number?", a: "Yes — a D-U-N-S profile is a key part of establishing business credit, and we set it up for you." },
      { q: "Will building business credit affect my personal credit?", a: "Properly structured business credit keeps your personal credit protected and separate." },
    ],
    related: ["business-funding", "credit-repair", "llc-formation"],
  },
  {
    slug: "credit-repair", category: "Finance", name: "Credit Repair",
    h1: "Credit Repair Services",
    metaTitle: "Credit Repair Services | Restore Your Credit | Alexa Financial",
    metaDescription: "Restore your personal credit with a proven, compliant framework. Dispute, remove and rebuild to unlock better rates, funding and opportunities.",
    keywords: "credit repair, fix my credit, credit restoration, dispute credit",
    intro: "Strong personal credit unlocks better rates, approvals and opportunities. Using a proven, compliant framework we dispute inaccuracies, remove what shouldn't be there and help you rebuild — so your credit works for your goals.",
    benefits: [
      "Compliant dispute and removal process",
      "Personalized credit improvement plan",
      "Guidance on rebuilding positive history",
      "Better rates and approval odds",
      "Foundation for funding and homeownership",
      "Ongoing education and support",
    ],
    process: [
      { title: "Analysis", desc: "We review your reports and identify inaccuracies and opportunities." },
      { title: "Dispute", desc: "We challenge questionable items with the bureaus and creditors." },
      { title: "Rebuild", desc: "We guide you to add positive, on-time history." },
      { title: "Leverage", desc: "We prepare you to qualify for funding and better terms." },
    ],
    faqs: [
      { q: "How long does credit repair take?", a: "Many clients see movement within 30–90 days; timelines vary by report complexity." },
      { q: "Is credit repair legal?", a: "Yes. Disputing inaccurate, unverifiable or outdated items is your legal right under the FCRA." },
      { q: "Can you guarantee results?", a: "No ethical provider can guarantee specific deletions, but our compliant framework maximizes your results." },
      { q: "Will this help me get funding?", a: "Improved personal credit strengthens both personal and business funding opportunities." },
    ],
    related: ["business-credit", "business-funding", "business-consulting"],
  },
  {
    slug: "business-consulting", category: "Finance", name: "Business Consulting",
    h1: "Business Consulting & Advisory",
    metaTitle: "Business Consulting Services | Growth Advisory | Alexa Financial",
    metaDescription: "1-on-1 business consulting to map your roadmap from formation to scale. Strategy, structure, funding and growth guidance from a trusted advisor.",
    keywords: "business consulting, business advisor, business growth strategy, startup consulting",
    intro: "Every great business needs a clear roadmap. Our 1-on-1 consulting helps you structure, fund and scale with confidence — turning your vision into an executable plan backed by a partner who's guided thousands of entrepreneurs.",
    benefits: [
      "Personalized growth roadmap",
      "Structure and compliance strategy",
      "Funding and credit planning",
      "Brand and web presence guidance",
      "Accountability and ongoing support",
      "Clarity on your next best move",
    ],
    process: [
      { title: "Discovery", desc: "We understand your goals, industry and current position." },
      { title: "Strategy", desc: "We build a clear, prioritized roadmap." },
      { title: "Execution", desc: "We help you implement structure, credit and funding steps." },
      { title: "Scale", desc: "We advise on growth, systems and expansion." },
    ],
    faqs: [
      { q: "Who is business consulting for?", a: "New and growing entrepreneurs who want expert guidance on structure, funding and scaling." },
      { q: "How are sessions delivered?", a: "We offer private consultations by phone or video, tailored to your schedule." },
      { q: "Can you help me start from zero?", a: "Absolutely — we guide you from idea and formation all the way to funded and growing." },
      { q: "Do you work with businesses outside Maryland?", a: "Yes, we advise entrepreneurs nationwide." },
    ],
    related: ["llc-formation", "business-funding", "seo-optimization"],
  },
  {
    slug: "website-design", category: "Web", name: "Professional Website Design",
    h1: "Professional Website Design",
    metaTitle: "Professional Website Design | Custom Business Sites | Alexa Financial",
    metaDescription: "Custom, conversion-focused website design for businesses. No templates. Mobile responsive, fast, SEO-ready sites that build trust and win clients.",
    keywords: "professional website design, custom website, business website design, web design",
    intro: "Your website is your first impression. We design custom, conversion-focused websites — no templates, no copy-and-paste — that make your brand look like a market leader and turn visitors into customers.",
    benefits: [
      "100% custom design (no templates)",
      "Mobile responsive and fast loading",
      "SEO-ready structure from day one",
      "Contact forms and WhatsApp integration",
      "Brand-aligned, premium visual style",
      "Built to convert visitors into clients",
    ],
    process: [
      { title: "Discovery", desc: "We learn your brand, audience and goals." },
      { title: "Design", desc: "We craft a unique, premium design tailored to you." },
      { title: "Build", desc: "We develop a fast, responsive, SEO-ready site." },
      { title: "Launch", desc: "We launch, train you and optimize for conversions." },
    ],
    faqs: [
      { q: "Do you use templates?", a: "No. Every website is custom-designed for your business — no templates, no copy-and-paste." },
      { q: "Will my site be mobile responsive?", a: "Yes. Every site is built mobile-first, fast and responsive across all devices." },
      { q: "Is SEO included?", a: "Basic SEO is built into every site, with advanced SEO available on premium packages." },
      { q: "How long does a website take?", a: "Landing pages can launch in days; multi-page and e-commerce sites vary by scope." },
    ],
    related: ["landing-page-design", "custom-business-websites", "ecommerce-development", "seo-optimization"],
  },
  {
    slug: "custom-business-websites", category: "Web", name: "Custom Business Websites",
    h1: "Custom Business Websites",
    metaTitle: "Custom Business Website Development | Alexa Financial Multiservices",
    metaDescription: "Multi-page custom business websites that build credibility and attract customers. Unique design, mobile responsive, SEO-ready, with Google Maps and forms.",
    keywords: "custom business website, multi-page website, business web development",
    intro: "Grow beyond a single page with a multi-page custom website that tells your full story, builds credibility and attracts more customers — designed uniquely for your brand and engineered to perform.",
    benefits: [
      "3–5+ custom pages tailored to you",
      "Unique professional design",
      "Mobile responsive and fast",
      "Google Maps and contact forms",
      "Social media integration",
      "SEO-ready foundation",
    ],
    process: [
      { title: "Plan", desc: "We map your pages, content and goals." },
      { title: "Design", desc: "We create a cohesive, premium multi-page design." },
      { title: "Develop", desc: "We build responsive, optimized pages." },
      { title: "Launch", desc: "We publish, integrate and train you." },
    ],
    faqs: [
      { q: "How many pages will my site have?", a: "Typically 3–5 pages, scalable to more based on your needs." },
      { q: "Can you migrate my existing content?", a: "Yes, we can refresh and migrate your current content and branding." },
      { q: "Do you include contact forms?", a: "Yes — contact forms and integrations are standard." },
      { q: "Is it SEO-ready?", a: "Every custom site is built with an SEO-ready structure." },
    ],
    related: ["website-design", "ecommerce-development", "seo-optimization"],
  },
  {
    slug: "landing-page-design", category: "Web", name: "Landing Page Design",
    h1: "Landing Page Design",
    metaTitle: "Landing Page Design Services | High-Converting Pages | Alexa Financial",
    metaDescription: "Custom high-converting landing pages for new businesses. Mobile responsive, contact form, WhatsApp integration and basic SEO to get you online fast.",
    keywords: "landing page design, high converting landing page, one page website",
    intro: "Get online fast with a beautiful, high-converting landing page. Perfect for new businesses that want a professional presence, capture leads and start attracting customers today.",
    benefits: [
      "Custom, conversion-focused landing page",
      "Mobile responsive design",
      "Contact form and WhatsApp integration",
      "Basic SEO included",
      "Social media links",
      "Launch-ready in days",
    ],
    process: [
      { title: "Brief", desc: "We capture your offer, brand and goal." },
      { title: "Design", desc: "We craft a striking, conversion-first page." },
      { title: "Build", desc: "We develop it responsive and fast." },
      { title: "Launch", desc: "We publish and connect your leads." },
    ],
    faqs: [
      { q: "Who is a landing page best for?", a: "New businesses and campaigns that want a fast, professional online presence." },
      { q: "Can I upgrade later?", a: "Yes — we can expand your landing page into a full multi-page website anytime." },
      { q: "Does it capture leads?", a: "Yes, with a contact form and WhatsApp integration built in." },
      { q: "How fast can it launch?", a: "Most landing pages launch within a few business days." },
    ],
    related: ["website-design", "custom-business-websites", "seo-optimization"],
  },
  {
    slug: "ecommerce-development", category: "Web", name: "E-commerce Website Development",
    h1: "E-commerce Website Development",
    metaTitle: "E-commerce Website Development | Online Stores | Alexa Financial",
    metaDescription: "Sell online with a premium e-commerce website. Payment gateway integration, advanced design, advanced SEO and training to compete with larger companies.",
    keywords: "ecommerce website, online store development, ecommerce design, sell online",
    intro: "Ready to sell online? We build premium e-commerce websites with secure payment gateways, advanced design and advanced SEO — so you can compete with larger companies and grow revenue around the clock.",
    benefits: [
      "Full online store (e-commerce)",
      "Secure payment gateway integration",
      "Advanced animations and premium design",
      "Advanced SEO for product visibility",
      "Optional blog and content tools",
      "Hands-on website training",
    ],
    process: [
      { title: "Strategy", desc: "We plan your catalog, checkout and goals." },
      { title: "Design", desc: "We craft a premium, on-brand storefront." },
      { title: "Build", desc: "We develop the store, payments and SEO." },
      { title: "Launch", desc: "We launch, train you and optimize sales." },
    ],
    faqs: [
      { q: "Which payment methods can I accept?", a: "We integrate secure gateways to accept major cards and digital payments." },
      { q: "Can I manage products myself?", a: "Yes — we provide training so you can manage your catalog and orders." },
      { q: "Is the store SEO-optimized?", a: "Yes, advanced SEO is included to help your products get found." },
      { q: "Can you add a blog?", a: "Absolutely — an optional blog helps drive organic traffic and sales." },
    ],
    related: ["custom-business-websites", "seo-optimization", "website-design"],
  },
  {
    slug: "seo-optimization", category: "Web", name: "SEO Optimization",
    h1: "SEO Optimization Services",
    metaTitle: "SEO Optimization Services | Rank Higher on Google | Alexa Financial",
    metaDescription: "Advanced SEO optimization to help your business rank higher and attract more customers. On-page SEO, technical SEO, local SEO and content strategy.",
    keywords: "SEO optimization, search engine optimization, local SEO, rank on Google",
    intro: "Being found is being chosen. Our SEO optimization improves your titles, structure, speed, content and local presence so your business ranks higher on Google and attracts a steady flow of ready-to-buy customers.",
    benefits: [
      "On-page SEO (titles, meta, headings)",
      "Technical SEO and speed optimization",
      "Local SEO for your service areas",
      "Keyword and content strategy",
      "Image ALT text and schema markup",
      "Ongoing performance guidance",
    ],
    process: [
      { title: "Audit", desc: "We analyze your site, keywords and competitors." },
      { title: "Optimize", desc: "We improve on-page, technical and local SEO." },
      { title: "Content", desc: "We build a keyword-driven content plan." },
      { title: "Grow", desc: "We track rankings and refine for results." },
    ],
    faqs: [
      { q: "How long does SEO take to work?", a: "SEO is a compounding investment; meaningful gains typically appear over 3–6 months." },
      { q: "What is local SEO?", a: "Local SEO helps your business appear when nearby customers search for your services." },
      { q: "Do you optimize existing sites?", a: "Yes — we optimize both our builds and existing websites." },
      { q: "Is schema markup included?", a: "Yes, we add structured data to help search engines understand your business." },
    ],
    related: ["website-design", "custom-business-websites", "ecommerce-development"],
  },
];

export const getService = (slug) => SERVICES_SEO.find((s) => s.slug === slug);
export const STATE_SERVICES = ["llc-formation", "website-design"];
